import React, { useState, useRef, useEffect } from 'react';
import { FaSave, FaTimes, FaExclamationCircle } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import 'react-quill/dist/quill.snow.css';
import 'react-toastify/dist/ReactToastify.css';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'header': [1, 2, 3, false] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
};

const CreateFAQ = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
  });
  const [errors, setErrors] = useState({
    question: false,
    answer: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const questionRef = useRef(null);

  useEffect(() => {
    // Focus on question input on mount
    if (questionRef.current) {
      questionRef.current.focus();
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleAnswerChange = (value) => {
    setFormData(prev => ({ ...prev, answer: value }));
    
    // Clear error when user types
    if (errors.answer) {
      setErrors(prev => ({ ...prev, answer: false }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      question: !formData.question.trim(),
      answer: !formData.answer.trim() || formData.answer === '<p><br></p>'
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors).find(key => errors[key]);
      if (firstError === 'question' && questionRef.current) {
        questionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/faq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: formData.question.trim(),
          answer: formData.answer
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create FAQ');
      }

      const data = await response.json();
      toast.success('FAQ created successfully!');
      
      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/admin/faq');
      }
    } catch (error) {
      console.error('Create FAQ error:', error);
      toast.error(error.message || 'Failed to create FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/admin/faq');
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800">Create New FAQ</h2>
        <p className="text-gray-600 mt-1">Fill in the details below to create a new FAQ</p>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="p-6">
        <div className="mb-8">
          <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
            Question <span className="text-red-500 ml-1">*</span>
            {errors.question && (
              <span className="flex items-center ml-2 text-red-500 text-xs">
                <FaExclamationCircle className="mr-1" /> This field is required
              </span>
            )}
          </label>
          <input
            ref={questionRef}
            type="text"
            id="question"
            name="question"
            value={formData.question}
            onChange={handleChange}
            className={`w-full px-4 py-3 border ${
              errors.question ? 'border-red-500' : 'border-gray-300'
            } rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all`}
            placeholder="Enter the question"
            maxLength={255}
          />
          <div className="flex justify-between mt-1">
            {errors.question && (
              <p className="text-xs text-red-500">
                <FaExclamationCircle className="inline mr-1" />
                Please enter a question
              </p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {formData.question.length}/255 characters
            </p>
          </div>
        </div>

        <div className="mb-8">
          <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
            Answer <span className="text-red-500 ml-1">*</span>
            {errors.answer && (
              <span className="flex items-center ml-2 text-red-500 text-xs">
                <FaExclamationCircle className="mr-1" /> This field is required
              </span>
            )}
          </label>
          <div className={`rounded-lg border ${
            errors.answer ? 'border-red-500' : 'border-gray-300'
          } overflow-hidden`}>
            <ReactQuill
              theme="snow"
              value={formData.answer}
              onChange={handleAnswerChange}
              modules={quillModules}
              placeholder="Write your answer here..."
              className="bg-white"
              style={{ height: '250px' }}
            />
          </div>
          {errors.answer && (
            <p className="text-xs text-red-500 mt-1 flex items-center">
              <FaExclamationCircle className="mr-1" />
              Please provide an answer
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 mt-8">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="flex items-center px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <FaTimes className="mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                Create FAQ
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateFAQ;